import { api, buildStorefrontApiPath, rememberOrgSlug } from "@/lib/api";
import {
  peekGuestSessionId,
  clearGuestSession,
} from "@/hooks/useGuestSession";
import type {
  LoginInput,
  RegisterInput,
  StorefrontCustomer,
  StorefrontMePayload,
  StorefrontSessionPayload,
} from "./types";

function unwrapData<T>(input: unknown): T {
  const payload = input as { data?: T } | T;
  return ((payload as { data?: T })?.data ?? payload) as T;
}

export async function register(input: RegisterInput) {
  const guestSessionId = peekGuestSessionId();

  const payload = {
    fullName: (input.fullName ?? input.name ?? "").trim(),
    email: input.email.trim().toLowerCase(),
    password: input.password,
    ...(input.phone?.trim() ? { phone: input.phone.trim() } : {}),
    ...(guestSessionId ? { guestSessionId } : {}),
  };

  const res = await api.post(buildStorefrontApiPath("/auth/register"), payload);
  const data = unwrapData<StorefrontSessionPayload>(res.data);

  // El backend ya hizo await del merge (síncrono).
  // Limpiamos guestSessionId aquí para que el login() subsecuente
  // en RegisterPage no lo re-envíe (evita doble merge innecesario).
  if (guestSessionId) {
    clearGuestSession();
  }

  rememberOrgSlug(data.organization.slug);
  return data;
}

export async function login(
  emailOrInput: string | LoginInput,
  password?: string,
  totp?: string,
) {
  const input: LoginInput =
    typeof emailOrInput === "string"
      ? {
          email: emailOrInput,
          password: password ?? "",
          ...(totp ? { totp } : {}),
        }
      : emailOrInput;

  const guestSessionId = peekGuestSessionId();

  const res = await api.post(buildStorefrontApiPath("/auth/login"), {
    email: input.email.trim().toLowerCase(),
    password: input.password,
    ...(input.totp ? { totp: input.totp } : {}),
    ...(guestSessionId ? { guestSessionId } : {}),
  });

  const data = unwrapData<StorefrontSessionPayload>(res.data);
  rememberOrgSlug(data.organization.slug);
  return data;
}

export async function refresh() {
  try {
    const res = await api.post(buildStorefrontApiPath("/auth/refresh"));
    const data = unwrapData<StorefrontSessionPayload>(res.data);
    rememberOrgSlug(data.organization.slug);
    return data;
  } catch (error: any) {
    const status = error?.response?.status;
    if (status === 401) return null;
    throw error;
  }
}

export async function logout() {
  try {
    await api.post(buildStorefrontApiPath("/auth/logout"));
  } catch (error: any) {
    const status = error?.response?.status;
    if (status === 401 || status === 204) return;
    throw error;
  }
}

export async function me() {
  const res = await api.get(buildStorefrontApiPath("/auth/me"));
  const data = unwrapData<StorefrontMePayload>(res.data);
  rememberOrgSlug(data.organization.slug);
  return data;
}


// ──────────────────────────────────────────────────────────────
// Sprint 5 Slice 2 — Customer email flows
// ──────────────────────────────────────────────────────────────

export type VerifyEmailResult = {
  status: "VERIFIED" | "ALREADY_VERIFIED";
  customer: StorefrontCustomer;
};

export async function verifyEmail(
  orgSlug: string,
  token: string,
): Promise<VerifyEmailResult> {
  const res = await api.post(
    buildStorefrontApiPath("/auth/verify-email", orgSlug),
    { token },
  );
  return unwrapData<VerifyEmailResult>(res.data);
}

export async function resendVerification(
  orgSlug: string,
  email: string,
): Promise<void> {
  await api.post(
    buildStorefrontApiPath("/auth/resend-verification", orgSlug),
    { email: email.trim().toLowerCase() },
  );
}

export async function requestPasswordReset(
  orgSlug: string,
  email: string,
): Promise<void> {
  await api.post(
    buildStorefrontApiPath("/auth/forgot-password", orgSlug),
    { email: email.trim().toLowerCase() },
  );
}

export async function confirmPasswordReset(
  orgSlug: string,
  token: string,
  newPassword: string,
): Promise<void> {
  await api.post(
    buildStorefrontApiPath("/auth/reset-password", orgSlug),
    { token, newPassword },
  );
}
